/**
 * Use the API in the StakeholderUtils module to handle the participants of a case.
 * @module StakeholderUtils
 */
/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import { C11nEnv } from '../interpreter/c11n-env';
import { invokeRestApi } from '../rest-client';
import { getVisibleFormFields } from '../utils/form-handler';

import type { ErrorResponse, ParticipantData } from './types';
import { updateETagToReduxStore } from './utils';

const GET_PARTICIPANTS = 'getAllParticipants';
const GET_PARTICIPANT_ROLES = 'getParticipantRoles';
const GET_PARTICIPANT_ROLE_VIEW = 'getParticipantRoleView';
const GET_PARTICIPANT = 'getParticipant';
const CREATE_PARTICIPANT = 'createParticipant';
const UPDATE_PARTICIPANT = 'updateParticipant';
const DELETE_PARTICIPANT = 'deleteParticipant';

/**
 * Obtains the list of participants for a specific case.
 * @function
 * @static
 * @example <caption>In this example, the API obtains the list of participants of a case whose ID is W-102.</caption>
 * PCore.getStakeholderUtils().getParticipants('ORG-APP-WORK W-102', getPConnect())
 * .then(participants => {
 *    // participants array
 * }).catch(err => {
 *   // errors
 * });
 *
 * @param caseID      The ID of the case pointing to pzInsKey value whose  list of participants must be obtained.
 * @param pConnectObj The pConnect object of the component from where the API is being called.
 * @returns A Promise that returns the list of participants linked to the specified case.
 */
export const getParticipants = (caseID: string, pConnectObj: C11nEnv): Promise<object> => {
  const queryPayload = {
    caseID
  };
  return invokeRestApi(GET_PARTICIPANTS, { queryPayload }, pConnectObj.getContextName())
    .then((response) => {
      return response?.data?.data.content.pyParticipants;
    })
    .catch((error) => {
      pConnectObj.reportError(': Error occurred during ajax call at getParticipants API : ', error.response);
    });
};

/**
 * Obtains the list of participant roles for a specific case.
 * @function
 * @static
 * @example <caption>In this example, the API obtains the list of participants of a case whose ID is W-102.</caption>
 * PCore.getStakeholderUtils().getParticipantRoles('ORG-APP-WORK W-102', getPConnect())
 * .then(roles => {
 *    // roles array
 * }).catch(err => {
 *   // errors
 * });
 * @param caseID      The ID of the case poiting to pzInsKey value whose  list of participants must be obtained.
 * @param pConnectObj The pConnect object of the component from where the API is being called.
 * @returns A Promise that returns the list of participant roles for the specified case.
 */
export const getParticipantRoles = (caseID: string, pConnectObj: C11nEnv): Promise<object> => {
  const queryPayload = {
    caseID
  };
  return invokeRestApi(GET_PARTICIPANT_ROLES, { queryPayload }, pConnectObj.getContextName())
    .then((response) => {
      return response?.data?.participant_roles;
    })
    .catch((error) => {
      pConnectObj.reportError(': Error occurred during ajax call at getParticipantRoles API : ', error.response);
    });
};

/**
 * Gets participant data by ID.
 * @function
 * @static
 * @example <caption>In this example, the API gets the data of participant with participant ID 'Interested_02', linked to a case whose ID is W-102.</caption>
 * PCore.getStakeholderUtils().getParticipant('ORG-APP-WORK W-102','Interested_02', getPConnect())
 * .then(participantData => {
 *    // participantData
 * }).catch(err => {
 *   // errors
 * });
 * @param caseID          The ID of the case pointing to pzInsKey value whose  list of participants must be obtained.
 * @param participantID   The string containing the participant ID.
 * @param pConnectObj     The pConnect object of the component from where the API is being called.
 * @returns A Promise that returns the participant details for the specified ID.
 */
export const getParticipant = (caseID: string, participantID: string, pConnectObj: C11nEnv): Promise<object> => {
  const queryPayload = {
    caseID,
    participantID
  };

  return invokeRestApi(GET_PARTICIPANT, { queryPayload }, pConnectObj.getContextName())
    .then((response) => response?.data)
    .catch((error) => {
      pConnectObj.reportError(': Error occurred during ajax call at getParticipant API : ', error.response);
    });
};

/**
 * Creates a new participant.
 * @function
 * @static
 * @example <caption>In this example, the API creates a new participant "Sam Smith" of role "Interested" for a case whose ID is W-102.</caption>
 *
 * const participantData = {
 *  "content":{
 *    "pyFirstName":"Sam",
 *    "pyLastName":"Smith",
 *    "pyEmail1":"samsmith@test.com",
 *    "pyPhoneNumber":"+11234567899",
 *    "pyTitle":"Developer"
 *    }
 *  };
 * PCore.getStakeholderUtils().createParticipant('ORG-APP-WORK W-102','Interested', participantData, getPConnect())
 * .then(newParticipantData => {
 *    // newParticipantData
 * }).catch(err => {
 *   // errors
 * });
 * @param caseID              The ID of the case pointing to pzInsKey value whose  list of participants must be obtained.
 * @param participantRoleID   The string containing the new participant role Id.
 * @param participantData     Data object containing participant details.
 * @param pConnectObj         The pConnect object of the component from where the API is being called.
 * @returns A Promise that returns data of participant created for the case.
 */
export const createParticipant = async (
  caseID: string,
  participantRoleID: string,
  participantData: ParticipantData,
  pConnectObj: C11nEnv,
  transientContext?: string
) => {
  const queryPayload = {
    caseID
  };

  const body = {
    ...participantData,
    participantRoleID
  };

  const headers = {
    'if-match': pConnectObj.getCaseSummary().headers.etag
  };

  return new Promise<void>((resolve, reject) => {
    invokeRestApi(CREATE_PARTICIPANT, { body, queryPayload, headers }, pConnectObj.getContextName())
      .then((response) => {
        updateETagToReduxStore(response.headers, pConnectObj);
        resolve();
      })
      .catch((error: ErrorResponse) => {
        error.response.data.errorDetails?.forEach((errorDetails) => {
          errorDetails.erroneousInputOutputFieldInPage = errorDetails.erroneousInputOutputFieldInPage.replace(
            /\.pyWorkParty\([^)]*\)/,
            ''
          );
        });
        const formFields = getVisibleFormFields(transientContext || pConnectObj.getContextName());
        pConnectObj.reportError(
          ': Error occurred during ajax call at createParticipant API : ',
          { ...error.response.data, formFields },
          transientContext
        );
        reject(error.response);
      });
  });
};

/**
 * Updates the details of a participant for a case.
 * @function
 * @static
 * @example <caption>In this example, the API updates an existing participant with ID "Interested_02" of a case whose ID is W-102.</caption>
 * const updatedParticipantData = {
 *  "content": {
 *    "pyFirstName":"Sam",
 *    "pyLastName":"Smith"
 *  }
 * };
 * PCore.getStakeholderUtils().updateParticipant('ORG-APP-WORK W-102','Interested_02', updatedParticipantData, getPConnect())
 * .then(updatedParticipantData => {
 *    // updatedParticipantData
 * }).catch(err => {
 *   // errors
 * });
 * @param caseID           The ID of the case pointing to pzInsKey value whose  list of participants must be obtained.
 * @param participantID    The ID of the participant to edit.
 * @param participantData  The object containing the params for updated participant data.
 * @param pConnectObj      The pConnect object of the component from where the API is being called.
 * @returns A Promise that returns data of participant updated for the case.
 */
export const updateParticipant = async (
  caseID: string,
  participantID: string,
  participantData: ParticipantData,
  pConnectObj: C11nEnv,
  transientContext?: string
) => {
  const queryPayload = {
    caseID,
    participantID
  };

  const headers = {
    'if-match': pConnectObj.getCaseSummary().headers.etag
  };

  return new Promise<void>((resolve, reject) => {
    invokeRestApi(UPDATE_PARTICIPANT, { body: participantData, queryPayload, headers }, pConnectObj.getContextName())
      .then((response) => {
        updateETagToReduxStore(response.headers, pConnectObj);
        resolve();
      })
      .catch((error: ErrorResponse) => {
        error.response.data.errorDetails?.forEach((errorDetails) => {
          errorDetails.erroneousInputOutputFieldInPage = errorDetails.erroneousInputOutputFieldInPage.replace(
            /\.pyWorkParty\([^)]*\)/,
            ''
          );
        });
        const formFields = getVisibleFormFields(transientContext || pConnectObj.getContextName());
        pConnectObj.reportError(
          ': Error occurred during ajax call at updateParticipant API : ',
          { ...error.response.data, formFields },
          transientContext
        );
        reject(error.response);
      });
  });
};

/**
 * Deletes an existing participant linked to a case
 * @function
 * @static
 * @example <caption>In this example, the API deletes an existing participant with ID "Interested_02" of a case whose ID is W-102.</caption>
 * PCore.getStakeholderUtils().deleteParticipant('ORG-APP-WORK W-102', 'Interested_02', getPConnect())
 * .then(updatedParticipantData => {
 *    // updatedParticipantData
 * }).catch(err => {
 *   // errors
 * });
 * @param caseID           The ID of the case pointing to pzInsKey value whose list of participants must be obtained.
 * @param participantID    The ID of the participant to delete. Eg: Owner_01, Interested_01, Customer
 * @param pConnectObj      The pConnect object of the component from where the API is being called.
 */
export const deleteParticipant = (caseID: string, participantID: string, pConnectObj: C11nEnv) => {
  const queryPayload = {
    caseID,
    participantID
  };

  const headers = {
    'if-match': pConnectObj.getCaseSummary().headers.etag
  };

  return new Promise<void>((resolve, reject) => {
    invokeRestApi(DELETE_PARTICIPANT, { queryPayload, headers }, pConnectObj.getContextName())
      .then((response) => {
        updateETagToReduxStore(response.headers, pConnectObj);
        resolve();
      })
      .catch((error) => {
        pConnectObj.reportError(': Error occurred during ajax call at deleteParticipant API : ', error.response);
        reject(error.response);
      });
  });
};

/**
 * Obtains the view for a participant role for a case.
 * @function
 * @static
 * @example <caption>In this example, the API obtains the list of participants of a case whose ID is W-102 and role 'Owner'.</caption>
 * PCore.getStakeholderUtils().getRoleView('ORG-APP-WORK W-102', 'Owner', getPConnect())
 * .then(view => {
 *    // role view
 * }).catch(err => {
 *   // errors
 * });
 * @param caseID               The ID of the case pointing to pzInsKey value whose  list of participants must be obtained.
 * @param participantRoleID    The ID of participant role whose view must be obtained.
 * @param pConnectObj          The pConnect object of the component from where the API is being called.
 * @returns A Promise that returns the details of the specified participant role.
 */
export const getRoleView = (caseID: string, participantRoleID: string, pConnectObj: C11nEnv): Promise<object> => {
  const queryPayload = { caseID, participant_roleID: participantRoleID };
  return invokeRestApi(GET_PARTICIPANT_ROLE_VIEW, { queryPayload }, pConnectObj.getContextName())
    .then((response) => response?.data)
    .catch((error) => {
      pConnectObj.reportError(': Error occurred during ajax call at fetchTaggedcases API : ', error.response);
    });
};

export default {
  getParticipants,
  getRoleView,
  getParticipantRoles,
  getParticipant,
  createParticipant,
  updateParticipant,
  deleteParticipant
};
